import { Offcanvas } from 'react-bootstrap';
import { Formik } from 'formik';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import HistoryDeadlineForm from './HistoryDeadlineForm';
import {
    submitHistoryDeadline,
    useHistoryDeadline,
    type HistoryDeadlineFormValues,
} from './model';

interface OffHistoryDeadlineProps {
    show: boolean;
    setShow: (show: boolean) => void;
    objectId: number;
    objectDeadline: number;
    onSuccess: (objectDeadline: number) => Promise<void>;
}

const toIsoDate = (millis: number) => {
    if (!millis) return '';
    return DT.fromMillis(Number(millis), { zone: 'local' }).toISODate() || '';
};

const OffHistoryDeadline = ({
    show,
    setShow,
    objectId,
    objectDeadline,
    onSuccess,
}: OffHistoryDeadlineProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { history, loading } = useHistoryDeadline(objectId, show);

    const handleClose = () => setShow(false);

    const initialValues: HistoryDeadlineFormValues = {
        deadline: toIsoDate(objectDeadline),
        comment: '',
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="off-history-deadline">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('historyDeadline.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={(values, helpers) =>
                        submitHistoryDeadline(values, helpers, {
                            objectId,
                            t,
                            showToast,
                            onSuccess: async (nextDeadline) => {
                                await onSuccess(nextDeadline);
                                setShow(false);
                            },
                        })
                    }
                >
                    {({
                        handleSubmit,
                        isSubmitting,
                        values,
                        setFieldValue,
                        errors,
                    }) => (
                        <HistoryDeadlineForm
                            values={values}
                            setFieldValue={setFieldValue}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            deadlineError={errors.deadline ? String(errors.deadline) : ''}
                            commentError={errors.comment ? String(errors.comment) : ''}
                            history={history}
                            historyLoading={loading}
                            autofocus={show}
                        />
                    )}
                </Formik>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffHistoryDeadline;
